import type { Command, CommandContext } from "./Command";
import { getComponent, withComponent } from "../../world/Entity";
import type { EntityId } from "../../world/Entity";

/** Combine a scene artifact with an inventory item, per recipes.json (loaded into context.recipes). */
export class CombineItemsCommand implements Command {
  constructor(
    private readonly entityId: EntityId,
    private readonly inventoryInstanceId: string,
  ) {}

  execute(context: CommandContext): void {
    const entity = context.world.getEntity(this.entityId);
    if (!entity) throw new Error(`Unknown entity "${this.entityId}"`);
    const artifactComponent = getComponent(entity, "artifact");
    if (!artifactComponent) throw new Error(`Entity "${this.entityId}" is not an artifact`);

    const heldItem = context.world.inventory.items.find((i) => i.instanceId === this.inventoryInstanceId);
    if (!heldItem) throw new Error(`Inventory item "${this.inventoryInstanceId}" not found`);

    const combo = context.recipes?.findRecipe(artifactComponent.definitionId, heldItem.definitionId);

    if (!combo) {
      context.events.emit("combine:failed", { entityId: this.entityId, withInstanceId: this.inventoryInstanceId });
      return;
    }

    context.world.updateEntity(this.entityId, (e) => ({
      ...withComponent(
        withComponent(e, "artifact", (a) => ({ ...a, definitionId: combo.produces })),
        "renderable",
        (r) => ({ ...r, asset: combo.produces }),
      ),
      definitionId: combo.produces,
    }));

    context.world.removeFromInventory(this.inventoryInstanceId);
    context.events.emit("combine:success", { entityId: this.entityId, produces: combo.produces });
  }
}
